import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Layout, PageHero } from "@/components/site/Layout";
import { Seo } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { breadcrumbJsonLd, faqPageJsonLd, serviceJsonLd } from "@/lib/seoSchemas";
import { getLocalPage, getLocalPagesForService } from "@/lib/localSeo";
import { fallbackServices } from "@/lib/serviceData";
import { ChevronRight, HelpCircle, MapPin } from "lucide-react";

/**
 * /hizmet/:slug/:bolge — ilçe × hizmet landing sayfası.
 *
 * Yalnızca Türkçe yayınlanır: içerik yerel arama niyetine yazıldığı için çeviri
 * kopyaları hem değersiz hem de kanibalizasyon riski. Bu yüzden Seo'ya trOnly
 * geçiliyor (canonical TR'ye sabit, hreflang basılmıyor) ve rota yalnızca kök
 * (TR) ağacına bağlanıyor.
 *
 * Tanımsız bir bölge istenirse ana hizmet sayfasına düşürülür — böylece yanlış
 * yazılmış/eski bir URL 404 yerine ilgili içeriğe gider.
 */
export default function LocalLanding() {
  const { slug, bolge } = useParams<{ slug: string; bolge: string }>();
  const page = getLocalPage(slug, bolge);

  if (!page) return <Navigate to={`/hizmet/${slug ?? ""}`} replace />;

  const service = fallbackServices.find((s) => s.slug === page.hizmet);
  // Sayfa TR-only olduğu için doğrudan title_tr — locale'e göre seçim yapılmıyor.
  const serviceName = service?.title_tr ?? page.hizmet;
  const path = `/hizmet/${page.hizmet}/${page.bolge}`;
  const siblings = getLocalPagesForService(page.hizmet).filter((p) => p.bolge !== page.bolge);

  return (
    <Layout>
      <Seo
        trOnly
        path={path}
        title={page.title}
        description={page.description}
        jsonLd={[
          serviceJsonLd({
            slug: page.hizmet,
            path,
            name: page.title,
            description: page.description,
            serviceType: serviceName,
            areaServed: {
              "@type": "AdministrativeArea",
              name: page.bolgeAdi,
              containedInPlace: { "@type": "City", name: "İstanbul" },
            },
          }),
          breadcrumbJsonLd([
            { name: "Ana Sayfa", path: "/" },
            { name: "Hizmetlerimiz", path: "/hizmetlerimiz" },
            { name: serviceName, path: `/hizmet/${page.hizmet}` },
            { name: page.bolgeAdi, path },
          ]),
          ...(page.faqs.length > 0
            ? [faqPageJsonLd(page.faqs.map((f) => ({ question: f.q, answer: f.a })))]
            : []),
        ]}
      />

      <PageHero title={page.h1} subtitle={page.lede} variant="band" />

      <article className="container-page py-14 md:py-16 max-w-4xl">
        {/* Görünür breadcrumb — JSON-LD ile aynı zinciri kullanıcıya da gösterir */}
        <nav aria-label="Konum" className="mb-10 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-accent transition-smooth">Ana Sayfa</Link>
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <Link to={`/hizmet/${page.hizmet}`} className="hover:text-accent transition-smooth">{serviceName}</Link>
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="text-foreground/80">{page.bolgeAdi}</span>
        </nav>

        <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-primary prose-strong:text-primary prose-p:text-foreground/85 prose-li:text-foreground/85 prose-a:text-accent prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown>{page.body}</ReactMarkdown>
        </div>

        {page.faqs.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <HelpCircle className="h-6 w-6 text-accent" />
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary m-0">
                {page.bolgeAdi} için sık sorulan sorular
              </h2>
            </div>
            <div className="space-y-3">
              {page.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-5 shadow-card open:shadow-elevated transition-smooth">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-display text-base md:text-lg font-semibold text-primary">
                    <span>{f.q}</span>
                    <span className="text-accent text-2xl leading-none transition-transform group-open:rotate-45 shrink-0">+</span>
                  </summary>
                  <p className="mt-3 text-sm md:text-base text-foreground/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {siblings.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-5">
              <MapPin className="h-5 w-5 text-accent" />
              <h2 className="font-display text-xl md:text-2xl font-bold text-primary m-0">
                Diğer bölgeler
              </h2>
            </div>
            <ul className="grid gap-2 sm:grid-cols-2">
              {siblings.map((p) => (
                <li key={p.bolge}>
                  <Link
                    to={`/hizmet/${p.hizmet}/${p.bolge}`}
                    className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-primary shadow-card hover:border-accent/50 hover:shadow-elevated transition-smooth"
                  >
                    <span>{p.h1}</span>
                    <ChevronRight className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-12 rounded-2xl bg-secondary/60 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-xl font-semibold text-primary">
              {page.bolgeAdi} için ücretsiz keşif
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Yerinde keşif yapıp ihtiyaca göre teklif hazırlıyoruz. Telefon: 0216 222 11 44
            </p>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent shrink-0">
            <Link to="/teklif-iste">Teklif İsteyin</Link>
          </Button>
        </div>
      </article>
    </Layout>
  );
}
